import { lazy, Suspense } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";

const MobileStage = lazy(() =>
  import("~/three/MobileStage").then((m) => ({ default: m.MobileStage })),
);

interface Project {
  id: string;
  title: string;
  blurb: string;
  tags: readonly string[];
}

interface ProjectWithLink extends Project {
  link?: string;
}

const PROJECTS: readonly ProjectWithLink[] = [
  {
    id: "cubek",
    title: "Cubek",
    blurb:
      "Rubik's cube simulator on Google Play — Unity-built native Android with AR Mode and AI Solver. The cube on the home page is its visual descendant.",
    tags: ["Unity", "Android", "Play Store"],
    link: "https://play.google.com/store/apps/details?id=com.JGSON.Cubek",
  },
  {
    id: "guia-guine",
    title: "Guia Guiné",
    blurb:
      "Mobile guide / reference app on Google Play — independent JGson release. // TODO João: one-line scope (region or topic the guide covers).",
    tags: ["Android", "Play Store"],
    link: "https://play.google.com/store/apps/details?id=com.jgson.guiaguine",
  },
];

export function Mobile() {
  const face = FACE_BY_ID.mobile;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-56 w-full overflow-hidden border-b border-jg-border/40 sm:h-72">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-mobile/15 to-transparent"
            />
          }
        >
          <MobileStage />
        </Suspense>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          Native <span style={{ color: face.themeColor }}>Android</span>,
          shipped and supported
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          Kotlin + Jetpack Compose for the modern stuff, Java for the
          long-running ships. Play Store release trains end to end.
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              What we build
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">Greenfield apps:</span> Kotlin
                + Jetpack Compose, Material 3, Hilt, Room, Coroutines + Flow.
              </li>
              <li>
                <span className="text-jg-fg">Legacy modernization:</span>{" "}
                migrating Java + XML codebases to Compose-first
                architectures incrementally.
              </li>
              <li>
                <span className="text-jg-fg">Play Store ops:</span> release
                trains, internal-testing tracks, Play Console
                integration, crash + ANR triage.
              </li>
              <li>
                <span className="text-jg-fg">Engine integrations:</span>{" "}
                shipping Unity-rendered surfaces inside native shells
                (see Cubek).
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Stack
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {[
                "Kotlin",
                "Java",
                "Jetpack Compose",
                "Material 3",
                "Hilt",
                "Room",
                "Retrofit",
                "Coroutines",
                "Flow",
                "Play Console",
              ].map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-jg-border bg-jg-bg/40 px-2.5 py-0.5 text-xs text-jg-fg-muted"
                >
                  {t}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-jg-fg-muted">
              For cross-platform, see the{" "}
              <a
                href="/react-native"
                className="underline-offset-2 hover:underline"
                style={{ color: face.themeColor }}
              >
                React Native cubie
              </a>{" "}
              — same author, different toolchain.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h3
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Shipped apps
          </h3>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECTS.map((p) => {
              const card = (
                <div className="flex h-full flex-col gap-2 rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-4 transition-colors group-hover:border-jg-mobile/40">
                  <h4 className="font-display text-base font-semibold leading-tight">
                    {p.title}
                  </h4>
                  <p className="text-sm leading-relaxed text-jg-fg-muted">
                    {p.blurb}
                  </p>
                  <ul className="mt-1 flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <li
                        key={t}
                        className="rounded-full border border-jg-border bg-jg-bg/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-jg-fg-muted"
                      >
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              );
              return (
                <li key={p.id}>
                  {p.link ? (
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block h-full"
                    >
                      {card}
                    </a>
                  ) : (
                    <div className="group block h-full">{card}</div>
                  )}
                </li>
              );
            })}
            <li className="flex h-full flex-col gap-2 rounded-2xl border border-dashed border-jg-border bg-jg-bg-soft/20 p-4">
              <h4 className="font-display text-base font-semibold leading-tight text-jg-fg-muted">
                More on request
              </h4>
              <p className="text-sm leading-relaxed text-jg-fg-muted">
                Additional native-Android case studies are under NDA. Reach
                out via the Hire button for references.
              </p>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
